import { useLazyQuery } from "@apollo/client";
import { Box, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";

import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DownloadIcon from "../../../../assets/icons/DownloadIcon.svg";
import PingIcon from "../../../../assets/icons/PingIcon.svg";
import SpeedTestPlanSpeed from "../../../../assets/icons/SpeedTestPlanSpeed.svg";
import SpeedTestServer from "../../../../assets/icons/SpeedTestServer.svg";
import UploadIcon from "../../../../assets/icons/UploadIcon.svg";
import SpeedTestBackground from "../../../../assets/images/SpeedTestBackground.png";
import { isAdmin } from "../../../../Constants/constants";
import { getBannerMessageByKey } from "../../../../Services/helper";
import { Theme } from "../../../../Styles/GlobalTheme";
import { GET_SPEED_TEST_DETAILS, SPEED_TEST_HISTORY } from "../../query";
import { HomepageNewBanners } from "../HomepageNewBanners/HomepageNewBanners";
import NetworkHealthBanner from "./NetworkHealthBanner";
import SpeedDialGauge from "./SpeedDialGauge";
import SpeedMetricResult from "./SpeedMetricResult";
import SpeedTestHistory from "./SpeedTestHistory";

declare global {
  interface Window {
    slowSpeeedDetected: any;
  }
}

function NetworkTest({ outageDetailsLoading, plansServicesInfo }: any) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isBelow600 = useMediaQuery("(max-width:600px)");
  const customerDetails = useSelector((state: any) => state.customerInfoDetails);
  const { accountNumber } = customerDetails;
  const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
  const [openSpeedTestAccordion, setOpenSpeedTestAccordion] = useState(false);
  const [speedTestHistory, setSpeedTestHistory] = useState<any>([]);
  const { usi, isSMB, ziplyPlanName } = useSelector((state: any) => state.customerPlanDetails);
  const [downloadSpeed, setDownloadSpeed] = useState(0);
  const [uploadSpeed, setUploadSpeed] = useState(0);
  const [pingValue, setPingValue] = useState<any>(null);
  const [currentPlanSpeed, setCurrentPlanSpeed] = useState("");
  const [speedTestDetails, setSpeedTestDetails] = useState<any>(null);
  const [planMaxSpeed, setPlanMaxSpeed] = useState(1000);
  const [loadingDownloadGauge, setLoadingDownloadGauge] = useState(false);
  const [loadingUploadGauge, setLoadingUploadGauge] = useState(false);
  const [reTryCount, setReTryCount] = useState(0);
  const [loadingPingGauge, setLoadingPingGauge] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(true);
  const [isLessSpeed, setIsLessSpeed] = useState(false);
  const [isRunTestInProgress, setIsRunTestInProgress] = useState(false);
  const [speedTestError, setSpeedTestError] = useState(false);
  const [completedDialAnimations, setCompletedDialAnimations] = useState({
    ping: false,
    download: false,
    upload: false,
  });
  const sideCoreBannerMessages = useSelector(
    (state: any) => state?.sideCoreContent?.bannerMessages
  );
  const { isZiplyRouter, determineNetworkStatusDetails } = useSelector(
    (state: any) => state.networkStatusDetails
  );
  const SPEED_TEST_BANNER_MESSAGE = getBannerMessageByKey("speed-test", sideCoreBannerMessages);
  const LOW_SPEED_ISSUE_BANNER_MESSAGE = getBannerMessageByKey(
    "low-speed-issue",
    sideCoreBannerMessages
  );

  const NON_ZIPLY_ROUTER_BANNER_MESSAGE = getBannerMessageByKey(
    "non-ziply-router",
    sideCoreBannerMessages
  );
  const hasValidValue = (value: any): boolean => {
    if (value === null || value === undefined) return false;
    if (typeof value === "number") return Number.isFinite(value);
    const raw = String(value).trim();
    if (!raw) return false;
    const lower = raw.toLowerCase();
    return lower !== "nan" && lower !== "null" && lower !== "undefined";
  };

  const isValidHistoryTimestamp = (value: any): boolean => {
    if (!hasValidValue(value)) return false;
    const raw = String(value).trim();
    const normalized = raw.includes(".") ? raw.split(".")[0] : raw;
    return !Number.isNaN(new Date(normalized).getTime());
  };

  const isValidHistoryRecord = (record: any): boolean => {
    return (
      record &&
      hasValidValue(record.downloadSpeed) &&
      hasValidValue(record.uploadSpeed) &&
      hasValidValue(record.ping) &&
      isValidHistoryTimestamp(record.timestamp)
    );
  };

  const parseMetric = (value: any): number => {
    if (value === null || value === undefined) return 0;
    if (typeof value === "number") return Number.isFinite(value) ? value : 0;
    const raw = String(value).trim();
    if (!raw) return 0;
    const n = parseFloat(raw.replace(/,/g, ""));
    if (!Number.isFinite(n)) return 0;
    const lower = raw.toLowerCase();
    if (lower.includes("gbps") || lower.includes("gb")) return Math.round(n * 1000);
    if (lower.includes("kbps") || lower.includes("kb")) return Math.round(n / 1000);
    return Math.round(n);
  };

  const [getSpeedTestDetails, { loading: speedTestLoading }] = useLazyQuery(
    GET_SPEED_TEST_DETAILS,
    {
      fetchPolicy: "network-only",
      onError: () => {
        setSpeedTestError(true);
        setIsLessSpeed(false);
      },
    }
  );
  const [getSpeedTestHistory, { loading: speedTestHistoryLoading }] = useLazyQuery(
    SPEED_TEST_HISTORY,
    {
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        const rawHistory = data?.getSpeedTestHistory?.data ?? [];
        const history = rawHistory?.filter((record: any) => isValidHistoryRecord(record));
        setSpeedTestHistory(history);
      },
      onError: () => {},
    }
  );

  const resolvePlanMaxSpeed = (mbpsValue: number): number => {
    if (mbpsValue <= 1000) return 1000;
    if (mbpsValue <= 2000) return 2000;
    if (mbpsValue <= 5000) return 5000;
    return 8000;
  };

  const runSpeedTest = async (): Promise<void> => {
    setSpeedTestError(false);
    setDownloadSpeed(0);
    setUploadSpeed(0);
    setPingValue(null);
    setCurrentPlanSpeed("");
    setIsLessSpeed(false);
    setLoadingDownloadGauge(true);
    setLoadingUploadGauge(false);
    setLoadingPingGauge(false);

    try {
      const speedTestData = await getSpeedTestDetails({
        variables: {
          accountNumber,
          usi,
        },
      });

      const testDetails = speedTestData?.data?.speedTestDetailsV2?.testDetails;
      setSpeedTestDetails(testDetails);
      const planSpeedMbps = parseMetric(
        testDetails?.purchasedDownloadSpeedInMBPS ?? testDetails?.purchasedUploadSpeedInMBPS
      );
      const resolvedMaxSpeed = resolvePlanMaxSpeed(planSpeedMbps || 1000);
      const downloadTarget = parseMetric(testDetails?.actualDownloadSpeedInMBPS);
      const uploadTarget = parseMetric(testDetails?.actualUploadSpeedInMBPS);
      let ping = null;

      setPlanMaxSpeed(resolvedMaxSpeed);
      setCurrentPlanSpeed(
        testDetails?.purchasedProduct
          ? `Ziply ${String(testDetails?.purchasedProduct).replace(/_/g, " ")}`
          : ""
      );

      // Required sequence: download -> upload -> ping.
      await delay(600);
      setLoadingDownloadGauge(false);
      setLoadingUploadGauge(true);
      setLoadingPingGauge(true);
      setDownloadSpeed(downloadTarget);

      await delay(2800);
      // await delay(600);
      setLoadingUploadGauge(false);
      setUploadSpeed(uploadTarget);

      // await delay(2800);
      // // await delay(600);
      // setLoadingPingGauge(false);
      // if (testDetails?.ping) {
      //   setPingValue(parseMetric(testDetails.ping));
      // }
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingDownloadGauge(false);
      setLoadingUploadGauge(false);
      setLoadingPingGauge(false);
    }
  };

  const handleRunTest = () => {
    if (isRunTestInProgress) return;

    if (isZiplyRouter === false && determineNetworkStatusDetails?.customSpeedTestURL?.trim()) {
      window.open(
        determineNetworkStatusDetails?.customSpeedTestURL.trim(),
        "_blank",
        "noopener,noreferrer"
      );
      return;
    }
    setIsRunTestInProgress(true);
    setOpenSpeedTestAccordion(true);
    setHistoryExpanded(false);
    setCompletedDialAnimations({
      ping: false,
      download: false,
      upload: false,
    });
    getSpeedTestHistory({
      variables: {
        accountNumber,
        usi,
      },
    });
    if (!isAdmin) {
      runSpeedTest();
    }
  };

  const handleReRunTest = () => {
    if (reTryCount >= 1) {
      window.slowSpeeedDetected("D582223DCFE61", {
        accountNumber: customerDetails?.accountNumber,
        plan: ziplyPlanName,
        serviceArea: isSMB ? "Business" : "Residential",
        serviceStatus: "active",
        connectionStatus: "Unstable Signal Detected",
      });
    } else {
      setReTryCount((prev) => prev + 1);
      handleRunTest();
    }
  };

  const handleDialAnimationComplete = (mode: "ping" | "download" | "upload") => {
    setCompletedDialAnimations((prev) => ({
      ...prev,
      [mode]: true,
    }));
  };

  useEffect(() => {
    if (!openSpeedTestAccordion) return;
    if (!speedTestDetails) return;

    setIsLessSpeed(!speedTestDetails?.speedInlineWithPlan);
  }, [openSpeedTestAccordion, speedTestDetails]);

  useEffect(() => {
    if (!isRunTestInProgress) return;

    const isAnimationCompleted =
      //ping value is not present in the response, will enable once available
      // completedDialAnimations.ping &&
      completedDialAnimations.download && completedDialAnimations.upload;

    if (isAnimationCompleted) {
      setIsRunTestInProgress(false);
    }
  }, [completedDialAnimations, isRunTestInProgress]);

  return (
    <>
      <NetworkHealthBanner />

      <Box
        sx={{
          borderRadius: "8px",
          boxShadow: `0px 0px 8px 0px ${Theme.palette.customcolor.bannerShadow}`,
        }}>
        <HomepageNewBanners
          content={SPEED_TEST_BANNER_MESSAGE?.content}
          actionItem={isAdmin ? "CHECK HISTORY" : "RUN TEST"}
          disabled={isRunTestInProgress || speedTestLoading || isLessSpeed}
          onActionHandle={handleRunTest}
          bannerStyles={{ marginBottom: 0 }}
          bannerType="info"
        />
        {openSpeedTestAccordion && (
          <>
            {speedTestError ? (
              <Box py={4} display="flex" justifyContent="center">
                <Typography fontSize={14} fontWeight={500} color="#6B7280">
                  Speed test is unavailable.
                </Typography>
              </Box>
            ) : (
              <Box
                px={{ xs: 2, md: 3 }}
                pb={2}
                sx={{
                  backgroundImage: `url(${SpeedTestBackground})`,
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  // height: "600px",
                }}>
                <Box
                  sx={{
                    width: "fit-content",
                    px: "20px",
                    py: 1,
                    borderRadius: "0px 0px 100px 100px",
                    backgroundColor: "#E6F1FF",
                    color: "#0D64D8",
                    mx: "auto",
                    mb: 2,
                    fontWeight: 500,
                    fontSize: "16px",
                    textAlign: "center",
                  }}>
                  <Typography component="span" fontSize="16px" fontWeight={500} color="inherit">
                    Testing from Ziply Fiber router to the network
                  </Typography>
                  {(isRunTestInProgress || speedTestLoading) && (
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "flex-end",
                        gap: "2px",
                        ml: "2px",
                        pb: "4px",
                        verticalAlign: "text-bottom",
                        "& span": {
                          width: "3px",
                          height: "3px",
                          borderRadius: "50%",
                          backgroundColor: "#0D64D8",
                          display: "inline-block",
                          opacity: 0.35,
                          animation: "networkLoaderDot 1s ease-in-out infinite",
                        },
                        "& span:nth-of-type(2)": {
                          animationDelay: "0.25s",
                        },
                        "@keyframes networkLoaderDot": {
                          "0%, 100%": {
                            opacity: 0.35,
                            transform: "scale(0.9)",
                          },
                          "50%": {
                            opacity: 1,
                            transform: "scale(1)",
                          },
                        },
                      }}>
                      <span />
                      <span />
                    </Box>
                  )}
                </Box>

                {!speedTestLoading && (
                  <Stack
                    direction={isMobile ? "column" : "row"}
                    spacing={2}
                    justifyContent="space-between"
                    mt={0}
                    mb={3}>
                    {!!speedTestDetails?.targetServerIp?.trim() && (
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: "23.3939px",
                          backgroundColor: "#f8f9f92f",
                          border: "1px solid rgba(255, 255, 255, 0.08)",
                          boxShadow: "0px 1.2px 4.8px 0px #0000000D",
                          minHeight: 94,
                          display: "flex",
                          alignItems: "center",
                        }}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Box
                            sx={{
                              width: 32,
                              borderRadius: "50%",
                              backgroundColor: "#F3F4F6",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              "& img": {
                                display: "block",
                              },
                            }}>
                            <img src={SpeedTestServer} alt="Speed Test Server" />
                          </Box>
                          <Box>
                            <Typography fontSize={10} color="#667085">
                              Speed test server
                            </Typography>
                            <Typography
                              fontSize={12}
                              fontWeight={600}
                              color="#1D2939"
                              lineHeight={1.2}>
                              {speedTestDetails?.targetServerIp}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    )}

                    {!!ziplyPlanName?.trim() && (
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: "23.3939px",
                          backgroundColor: "#f8f9f92f",
                          border: "1px solid rgba(255, 255, 255, 0.08)",
                          boxShadow: "0px 1.2px 4.8px 0px #0000000D",
                          minHeight: 94,
                          display: "flex",
                          alignItems: "center",
                        }}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Box
                            sx={{
                              width: 32,
                              borderRadius: "50%",
                              backgroundColor: "#F3F4F6",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              "& img": {
                                display: "block",
                              },
                            }}>
                            <img src={SpeedTestPlanSpeed} alt="Speed Test Plan Speed" />
                          </Box>
                          <Box>
                            <Typography fontSize={10} color="#667085">
                              Current plan speed
                            </Typography>
                            <Typography
                              fontSize={12}
                              fontWeight={600}
                              color="#1D2939"
                              lineHeight={1.2}>
                              {ziplyPlanName}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    )}
                  </Stack>
                )}
                <Box
                  display="flex"
                  flexDirection={isBelow600 ? "column" : "row"}
                  justifyContent={
                    isBelow600 ? "center" : pingValue ? "space-between" : "space-around"
                  }
                  alignItems={"center"}
                  mb={3}
                  flexWrap={isBelow600 ? "nowrap" : "wrap"}
                  gap={2}>
                  {/* ping value is not present in the response, will enable once available */}
                  {/* {pingValue && (
                  <SpeedDialGauge
                    speed={pingValue}
                    maxSpeed={100}
                    size={180}
                    mode={"ping"}
                    unit="ms"
                    loading={loadingPingGauge}
                    isError={completedDialAnimations.ping && isLessSpeed}
                    isResultReady={completedDialAnimations.ping}
                    onAnimationComplete={handleDialAnimationComplete}
                  />
                )} */}
                  {/* Download */}
                  <SpeedDialGauge
                    speed={downloadSpeed}
                    maxSpeed={planMaxSpeed}
                    size={240}
                    mode={"download"}
                    unit="Mbps"
                    loading={loadingDownloadGauge}
                    isError={completedDialAnimations.download && isLessSpeed}
                    isResultReady={completedDialAnimations.download}
                    onAnimationComplete={handleDialAnimationComplete}
                  />
                  {/* Upload */}
                  <SpeedDialGauge
                    speed={uploadSpeed}
                    maxSpeed={planMaxSpeed}
                    size={240}
                    mode={"upload"}
                    unit="Mbps"
                    loading={loadingUploadGauge}
                    isError={completedDialAnimations.upload && isLessSpeed}
                    isResultReady={completedDialAnimations.upload}
                    onAnimationComplete={handleDialAnimationComplete}
                  />
                </Box>
                {!(isRunTestInProgress || speedTestLoading) && (
                  <Box
                    sx={{
                      position: "relative",
                      padding: 2,
                      borderRadius: "16px",
                      overflow: "hidden",
                      backgroundColor: "rgba(255, 255, 255, 0)",
                      boxShadow:
                        "inset 0 0 16.94px rgba(226, 226, 226, 0.28), inset 0 0 1.06px 4.24px rgba(255, 255, 255, 0.18)",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        inset: 0,
                        backdropFilter: "blur(25.42px)",
                        WebkitBackdropFilter: "blur(25.42px)",
                        maskImage: "linear-gradient(to bottom, rgba(0,0,0,0.17), rgba(0,0,0,1))",
                        WebkitMaskImage:
                          "linear-gradient(to bottom, rgba(0,0,0,0.17), rgba(0,0,0,1))",
                        pointerEvents: "none",
                      },
                    }}>
                    <Stack width={"100%"} justifyContent={"space-around"} direction={"row"}>
                      {/* ping value is not present in the response, will enable once available */}
                      {false && (
                        <>
                          {" "}
                          <SpeedMetricResult
                            icon={PingIcon}
                            label="Ping"
                            value={pingValue}
                            unit="ms"
                            isError={isLessSpeed}
                            subText="Ultra-fast response with stable low latency"
                          />
                          <Box
                            sx={{ height: "50px", width: "3px", backgroundColor: "#EEEEEE" }}></Box>
                        </>
                      )}
                      <SpeedMetricResult
                        icon={DownloadIcon}
                        label="Download Speed"
                        value={downloadSpeed}
                        unit="Mbps"
                        isError={isLessSpeed}
                        subText="Lightning-fast Downloads. Smooth. Stable. Instant."
                      />
                      <Box sx={{ height: "50px", width: "3px", backgroundColor: "#EEEEEE" }}></Box>
                      <SpeedMetricResult
                        icon={UploadIcon}
                        label="Upload Speed"
                        value={uploadSpeed}
                        unit="Mbps"
                        isError={isLessSpeed}
                        subText="Experience High-Speed Uploads with seamless low latency"
                      />
                    </Stack>
                  </Box>
                )}
              </Box>
            )}

            {isLessSpeed && !(isRunTestInProgress || speedTestLoading) && (
              <Box mb={2} px={2}>
                <HomepageNewBanners
                  content={LOW_SPEED_ISSUE_BANNER_MESSAGE.content}
                  actionItem={"RETRY TEST"}
                  onActionHandle={handleReRunTest}
                  bannerType={"warning"}
                />
              </Box>
            )}
            <SpeedTestHistory
              speedTestHistory={speedTestHistory}
              historyExpanded={historyExpanded}
              setHistoryExpanded={setHistoryExpanded}
              speedTestHistoryLoading={speedTestHistoryLoading}
            />
          </>
        )}
      </Box>
    </>
  );
}

export default NetworkTest;
